import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormGroup, LinearProgress, TextField, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { Close, Save as SaveIcon } from '@mui/icons-material';

import { enqueueSnackbarError } from 'lib/helpers';

import useSuccessSnackbar from 'hooks/useSuccessSnakbar';

import { NotificationTopic, updateNotification } from 'lib/models/notification';

export const defaultNotification: AppNotification = {
	title: {
		es: '',
	},
	message: {
		es: '',
	},
	topic: NotificationTopic.custom,
};

type Props = {
	onRefetch: () => void;
	onClose: () => void;
	defaultData: AppNotification;
};

function NotificationModal({ defaultData, onClose, onRefetch }: Props) {
	const { t } = useTranslation();

	const successSnackbar = useSuccessSnackbar();

	const [loading, setLoading] = useState<boolean>(false);
	const [notification, setNotification] = useState<AppNotification>(defaultData);

	const canSave = !!notification?.title.es && !!notification?.message.es;

	const onUpdate = (key: keyof AppNotification) => {
		return (event: any) => {
			if (event) {
				setNotification((current) => ({
					...(current || defaultNotification),
					[key]: { es: event.target.value },
				}));
			}
		};
	};

	async function save() {
		if (notification) {
			try {
				setLoading(true);
				await updateNotification({ ...notification });
				setNotification(defaultNotification);
				successSnackbar();
			} catch (error) {
				enqueueSnackbarError(error);
			}

			onRefetch();
			setLoading(false);
		}
	}

	return (
		<Dialog open={true} onClose={onClose}>
			{loading && <LinearProgress />}

			<DialogTitle>{t(defaultData._id ? 'common:editNotification' : 'common:createNotification')}</DialogTitle>

			<DialogContent sx={{ minWidth: '30vw' }}>
				{loading && <LinearProgress sx={{ mb: 3 }} />}

				{notification && (
					<FormGroup>
						<TextField
							id="title"
							label={t('common:title')}
							fullWidth={true}
							value={notification.title.es}
							variant="outlined"
							sx={{ mb: 3 }}
							onChange={onUpdate('title')}
							disabled={loading}
						/>

						<TextField
							id="description"
							label={t('common:description')}
							fullWidth={true}
							value={notification.message.es}
							variant="outlined"
							sx={{ mb: 3 }}
							onChange={onUpdate('message')}
							disabled={loading}
							multiline
						/>

						<Button
							variant="contained"
							color="primary"
							size="large"
							sx={{ m: 1 }}
							startIcon={<SaveIcon />}
							onClick={save}
							disabled={!canSave}
						>
							{t('common:save')}
						</Button>

						<Button
							variant="contained"
							color="secondary"
							size="large"
							sx={{ m: 1 }}
							startIcon={<Close />}
							onClick={onClose}
						>
							{t('common:cancel')}
						</Button>
					</FormGroup>
				)}
			</DialogContent>
		</Dialog>
	);
}

export default NotificationModal;
